import { Injectable } from '@angular/core';
import * as authLib from '../modules/shared/models/authenticationModels';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, lastValueFrom, of, throwError } from 'rxjs';
import {  falUserModel, userOrgModel } from '../modules/shared/models';
import { map, catchError, filter, take } from 'rxjs/operators';
import * as jwtDecode from "jwt-decode";
import { DialogService, ErrorLevels } from '../modules/shared/services/dialog.service';
import { Router } from '@angular/router';

import { AppConfig } from '../modules/shared/models/app-config';
import { environment } from '../../environments/environment';
import { ApiResultValues, IApiResult } from '../modules/shared/models/apiResultModels';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
    static serviceDescriptor = 'FixedAssetRegister';
    


    public currentUserSubject: BehaviorSubject<authLib.serviceAuthenticationResult>;
    public isAuthenticated: BehaviorSubject<boolean>;
    public isUserVerified: BehaviorSubject<boolean>;
    public isRefreshing = false;
    public refreshSubject: BehaviorSubject<authLib.uiAuthResult | null>;
    private actualToken: string | null = null;;
    public ssoIdentity: BehaviorSubject<authLib.SsoIdentity | null>;
    public IsAuthenticatedDetail: BehaviorSubject<authLib.AuthenticationStatusDetail>;

    private _authTokenStorageName = "precedingValues";
    private _refreshTokenStorageName = "underlyingTrend";
    private _currentUserStorageName = "currentUser";
    private _authSsoStorageName = "currentSSO";

    constructor(private http: HttpClient,
        private router: Router,
        private dialog: DialogService) {
        
        console.log("AuthService", "Constructor is firing");
        this.refreshSubject = new BehaviorSubject<authLib.uiAuthResult | null>(null);
        this.currentUserSubject = new BehaviorSubject<authLib.serviceAuthenticationResult>(JSON.parse(localStorage.getItem(this._currentUserStorageName)));
        this.ssoIdentity = new BehaviorSubject<authLib.SsoIdentity | null>(JSON.parse(localStorage.getItem(this._authSsoStorageName)));
        if (this.currentUserSubject.value) {
        this.isAuthenticated = new BehaviorSubject<boolean>(this.currentUserSubject.value.token !== null);
        //Setting this to always init to true.  It is for the server to decide the user needs to be verified,
        //  so this should only be set once the refresh of a token takes place and we know the response
        //  is current
        this.isUserVerified = new BehaviorSubject<boolean>(true);
        } else {
        this.isAuthenticated = new BehaviorSubject<boolean>(false);
        this.isUserVerified = new BehaviorSubject<boolean>(false);
        }
        
        //Create the observable
        this.IsAuthenticatedDetail = new BehaviorSubject<authLib.AuthenticationStatusDetail>({
            isAuthenticated: this.isAuthenticated.value,
            reason: authLib.AuthenticationStatusPurpose.Initialisation
        });
    }

    public get getCurrentUser(): falUserModel | null {
        return this.currentUserSubject.value ? this.currentUserSubject.value.user : null
    }

    public get getCurrentAuthToken(): string | null {
        //Transition to new paradigm 
        let token = localStorage.getItem(this._authTokenStorageName);
        if(!token){
            const user = this.currentUserSubject.value;
            if(user){
                token = user.token;
                localStorage.setItem(this._authTokenStorageName, token);
            }
        }
        return token;
    }

    public get getCurrentRefreshToken(): string | null{
        //Transition to new paradigm 
        let token = localStorage.getItem(this._refreshTokenStorageName);
        if(!token){
            const user = this.currentUserSubject.value;
            if(user){
                token = user.refreshToken;
                localStorage.setItem(this._refreshTokenStorageName, token);
            }
        }
        return token;
    }

    setUserVerified() {
        if (this.isAuthenticated.value) {
            this.getCurrentUser!.accountVerified = true;
            this.isUserVerified.next(true);
        }
    }


    async login(username: string, password: string): Promise<authLib.uiAuthResult> {
        const obs = new Observable<authLib.uiAuthResult>(o => {
            this.ssoIdentity.next(null);
            const u: authLib.serviceAuthenticationModel = {
             username,
             password,
             serviceDescriptor: AuthService.serviceDescriptor
            };
            
            this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}auth`, u).subscribe(authResult => {
                if (authResult.result === "Ok" || authResult.result === 'AwaitingVerification') {
                    const uiRes = this.handleLoginResult(authResult, authLib.AuthenticationStatusPurpose.Login);
                    o.next(uiRes);
                } else {
                    const res: authLib.uiAuthResult =
                        {
                            error: authResult.result,
                            ok: false,
                            requireVerification: false
                        };
                    o.next(res);
                }
            }, error => {
                this.dialog.createLog("Login failed", "The authentication service encountered an error while trying to validate your credentials.  The app cannot continue while this condition continues.  Please try again later. ", true);
            });
        });

        try{
            this.ssoIdentity.next(null);
            const u: authLib.serviceAuthenticationModel = {
             username,
             password,
             serviceDescriptor: AuthService.serviceDescriptor
            };
            
            
            var authResult = await lastValueFrom( this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}auth`, u).pipe(map(response => response)));
            if (authResult.result === "Ok" || authResult.result === 'AwaitingVerification') {
                const uiRes = this.handleLoginResult(authResult, authLib.AuthenticationStatusPurpose.Login);
                return uiRes;
            } else {
                const res: authLib.uiAuthResult =
                    {
                        error: authResult.result,
                        ok: false,
                        requireVerification: false
                    };
                return res
            }
        }catch{
            this.dialog.createLog("Login failed", "The authentication service encountered an error while trying to validate your credentials.  The app cannot continue while this condition continues.  Please try again later. ");
            return {
                error: 'Exception calling signin api',
                ok: false,
                requireVerification: false
            };
        }

    }

    async loginSSO(id: authLib.SsoIdentity): Promise<authLib.uiAuthResult> {
        const u = {
            email: id.email,
            provider: id.provider,
            uuid: id.uuid,
            serviceDescriptor: AuthService.serviceDescriptor,
            phone: id.phone,
            givenName: id.givenName,
            familyName: id.familyName,
            verified: id.verified
        };

        try{
            var authResult = await lastValueFrom(this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}authSso`, u));
            if (authResult.result === "Ok" || authResult.result === 'AwaitingVerification') {
                this.dialog.createLog("Login","Login ok");
                const uiRes = this.handleLoginResult(authResult, authLib.AuthenticationStatusPurpose.Login,u);
                return uiRes;
            } else {
                this.dialog.createLog("Loging", "AuthService: Login failure");
                return {
                    error: authResult.result,
                    ok: false,
                    requireVerification: false
                };
            }
        }catch{
            this.dialog.createLog("LoginSSO", "The authentication service encountered an error while trying to validate your credentials.  The app cannot continue while this condition continues.  Please try again later. ", true);
            return {
                error: authResult.result,
                ok: false,
                requireVerification: false
            }
        }
    }

    refreshToken(): BehaviorSubject<authLib.uiAuthResult> {
        if (!this.isRefreshing) {

            
            this.dialog.createLog("AuthService","refreshToken will attempt to refresh auth token");
            this.isRefreshing = true;
            this.refreshSubject.next(null);

            const current = this.currentUserSubject.value;

            const u: authLib.serviceRefreshAuthenticationModel = {
                authToken: this.getCurrentAuthToken,
                refreshToken: this.getCurrentRefreshToken,
                serviceDescriptor: current.user.serviceDescriptor
            };

            this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}refresh`, u)
                .pipe(
                    map(authRes => {
                        this.dialog.createLog("AuthService", "refresh has completed");
                        if (authRes.result === 'Ok' || authRes.result === 'AwaitingVerification') {
                            this.dialog.createLog("AuthService", "refresh was successful");
                            const uiRes = this.handleLoginResult(authRes, authLib.AuthenticationStatusPurpose.Refresh);
                            this.isRefreshing = false;
                            this.refreshSubject.next(uiRes);
                            return uiRes;
                        } else {
                            this.dialog.createLog("AuthService","refresh failed!!!");
                            this.isRefreshing = false;
                            const uiRes:authLib.uiAuthResult = {
                                error: authRes.result,
                                ok: false,
                                requireVerification: false
                            };
                            this.refreshSubject.next(uiRes);
                            return uiRes;
                        }
                    }),
                    catchError(error => {
                        this.dialog.createLog("refresh", "The authentication service encountered an error while trying to refresh your access token.  The app cannot continue while this condition continues.  Please try again later. ", true);
                        this.isRefreshing = false;
                        const uiRes: authLib.uiAuthResult = {
                            error : error,
                            ok: false,
                            requireVerification: false
                        };
                        this.refreshSubject.next(uiRes);
                        return throwError(() => new Error(error));
                    })
                ).subscribe();
            } else {
            this.dialog.createLog("AuthService","refreshToken is already waiting for the refresh to complete");
        }

        return this.refreshSubject;
    }


    clearToken() {
        this.actualToken = this.currentUserSubject.value.token;
        this.currentUserSubject.value.token = 'abcdedesfushu';
        localStorage.removeItem(this._authTokenStorageName);
        localStorage.removeItem(this._refreshTokenStorageName);
    }

    getUserOrganisations() {
        const url = `${AppConfig.settings.apiAuthUrl}Organisations`;
        return this.http.get<userOrgModel[]>(url).pipe(
            catchError(e => {
                this.dialog.createLog("getUserOrganisations", "An error occurred attempting to get your organisation list", true);
                return of<userOrgModel[]>([]);
            })
        );
    }

    getUserOrganisation(id: string) {
        const url = `${AppConfig.settings.apiValidateUrl}Organisation/${id}`;
        return this.http.get<IApiResult>(url).pipe(
            map(i => {
                const u = new userOrgModel();
                Object.assign(u, i.data);
                return u;
            }), 
            catchError(e => {
                this.dialog.createLog("getUserOrganisation", "An error occurred attempting to get your organisation details", true);
                return of<userOrgModel>(null);
            })
        );
    }

    async getUserSubscriptionUsage(): Promise<any> {
        const url = `${AppConfig.settings.apiValidateUrl}Organisations`;
        return firstValueFrom(this.http.get<userOrgModel[]>(url).pipe(
                catchError(e => {
                    this.dialog.reportError(ErrorLevels.Critical, "GetUserSubscriptionUsage", "An error occurred attempting to get your subscription usage", e);
                    return of<userOrgModel[]>(null);
                })
            )
        );
    }

    verifyAuthenticationStatus(): Observable<authLib.uiValidateAuthResult> {
        const obs = new Observable<authLib.uiValidateAuthResult>(o => {
        const token = this.getCurrentAuthToken;

        //Test one: must be a token
        if (!token) {
            this.dialog.createLog("VerifyAuthenticationStatus","Authentication token could not be found so verifyAuth will return false");
            this.logout(null);
            o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.Failed));
            return;
        }

        o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.Success));

        //Test two: decode token and compare expiry date with current date/time
        //DEV NOTE:
        //  This is not strictly speaking necessary.  If we attempt to access the API after the token has expired,
        //  there will be a 401 error.  Our interceptor can then attempt to refresh the token and call logout if
        //  the refresh does not work.
        //  However, by doing this bit of work, we can save ourselves a call to the server, simply to identify that
        //  there might be the need to refresh the token.  It seems more sensible to allow this code to run on the
        //  client, so we save a couple of trips to the server just for the sake of cleanliness.
        //   const jwtToken = this.getDecodedAccessToken(token);
        //   if (!jwtToken) {
        //     this.errSvc.reportError(null, "VerifyAuth could not decode the access token currently story for the user.  Will logout and start again.", false);
        //     o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.Failed));
        //     return;
        //   }
        //   const now = Date.now().valueOf() / 1000;
        //   const then = jwtToken.exp;
        //   if (then < now) {
        //     //token has expired - for now we will return a false but after testing, revise to request a refresh token
        //     this.errSvc.reportError(null, `Token expiry date ${then} has been compared to be older than ${now}.  VerifyAuth will attempt to refresh the token`, false);
        //     this.refreshToken().pipe(
        //       filter(res => res !== null),
        //       take(1)
        //       ).subscribe(res => {
        //       if (res.ok) {
        //         o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.Success));
        //       } else {
        //         this.errSvc.reportError(null, "VerifyAuth failed to refresh token and so you will be logged out.", false);
        //         o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.RefreshFailed));
        //       }
        //     });
        //   } else {
        //     o.next(new authLib.uiValidateAuthResult(null, authLib.ValidateAuthResult.Success));
        //   }
        });


        return obs;
    }

    private getDecodedAccessToken(token: string): any {
        try {
        return jwtDecode.jwtDecode(token);
        }
        catch (Error) {
        return null;
        }
    }

    logout(params: string, reason?: authLib.AuthenticationStatusPurpose, noRedirect?: boolean) {
        this.dialog.createLog("AuthService","logout: About to move to new page.  Params and current url", params, this.router.url);

        if(!params && (this.router.url === '/' || noRedirect)){
            this.logoutClearInternals();
        }else{
            this.router.navigate([environment.loginUrl], { queryParams: { returnUrl: params } }).then(() => {
                this.logoutClearInternals();
            });
        }
    }

    private logoutClearInternals(reason?: authLib.AuthenticationStatusPurpose){
        if (this.isAuthenticated.value) {
            const url = `${AppConfig.settings.apiAuthUrl}signOut/${AuthService.serviceDescriptor}`;
            this.http.post<boolean>(url, null).subscribe(r => {
                this.dialog.createLog("AuthService","logout: Invalidate API returned " + r);
            });
        }

        this.dialog.createLog("AuthService","logout: Clearing local storage");
        localStorage.removeItem(this._currentUserStorageName);
        localStorage.removeItem(this._authSsoStorageName);
        localStorage.removeItem(this._authTokenStorageName);
        localStorage.removeItem(this._refreshTokenStorageName);
        
        this.dialog.createLog("AuthService","logout: Send current user UI update messages");
        this.currentUserSubject.next(null);
        this.ssoIdentity.next(null);
        this.dialog.createLog("AuthService","logout: Call API to invalidate tokens");

        this.dialog.createLog("AuthService","logout: Send current user auth update message");
        this.isAuthenticated.next(false);
        const detail: authLib.AuthenticationStatusDetail = {
            isAuthenticated: false,
            reason: reason ? reason : authLib.AuthenticationStatusPurpose.Logout
        };
        this.IsAuthenticatedDetail.next(detail);
    }


    changePassword(model: authLib.userPasswordUpdateModel) {
        const url = `${AppConfig.settings.apiAuthUrl}changePassword`;
        model.userId = this.getCurrentUser.id;

        return this.http.post<authLib.serviceAuthenticationResult>(url, model).pipe(
        map(result => {
            if (result.result === 'Ok') {
            //return this.validate(result);
            const uiRes = this.handleLoginResult(result, authLib.AuthenticationStatusPurpose.PasswordChange);
            return uiRes;
            }
            //handle other issues...
            const res: authLib.uiAuthResult = {
                error: result.result,
                ok: false,
                requireVerification: false
            };
            return of<authLib.uiAuthResult>(res);
        }),
        catchError(e => {
            this.dialog.reportError(ErrorLevels.Critical, "Change password", "An error occurred while changing your password");
            const res: authLib.uiAuthResult = {
                error:"An error has occurred",
                requireVerification: false,
                ok: false
            };
            
            return of<authLib.uiAuthResult>(null);
        })
        );
    }

    unique(email: string) {
        const url = `${AppConfig.settings.apiAuthUrl}unique`;
        const data = { email: email };
        return this.http.post<boolean>(url, data).
        pipe(
            catchError(e => {
            this.dialog.reportError(ErrorLevels.Critical, "Confirming available email address", "An error occurred while checking the email address is permitted", true);
            return of<boolean>(false);
            })
        );
    }


    private handleLoginResult(res: authLib.serviceAuthenticationResult, reason: authLib.AuthenticationStatusPurpose, ssoId?: authLib.SsoIdentity) {
        //Hold onto the logged in user for easier reuse
        localStorage.setItem(this._currentUserStorageName, JSON.stringify(res));
        localStorage.setItem(this._authTokenStorageName, res.token);
        localStorage.setItem(this._refreshTokenStorageName, res.refreshToken)

        if (reason === authLib.AuthenticationStatusPurpose.Login) {
            if (ssoId) {
                localStorage.setItem(this._authSsoStorageName, JSON.stringify(ssoId));
            } else {
                localStorage.removeItem(this._authSsoStorageName);
            }
        }
        
        const uiRes : authLib.uiAuthResult = {
            ok: true,
            requireVerification: res.result === 'AwaitingVerification',    
        }

        this.isUserVerified.next(!uiRes.requireVerification);
        this.currentUserSubject.next(res);
        this.isAuthenticated.next(true);
        
        const detail: authLib.AuthenticationStatusDetail = {
            reason,
            isAuthenticated: true
        };
        this.IsAuthenticatedDetail.next(detail);
        return uiRes;
    }


    userVerifyEmail(token: string) {
        const url = `${AppConfig.settings.apiAuthUrl}verify/${token}`;
        return this.http.post<boolean>(url, null).pipe(
        map(r => {
            if (r) {
            //this.getCurrentUser.accountVerified = true;
            this.isUserVerified.next(true);
            this.currentUserSubject.value.user.accountVerified = true;
            localStorage.setItem(this._currentUserStorageName, JSON.stringify(this.currentUserSubject.value));
            }
            return r;
        }),
        catchError(e => {
            this.dialog.reportError(ErrorLevels.Critical, "Verify email addres", "An error occured while verifying the user's email address ", e);
            return of<boolean>(false);
        })
        )
    }


    resendAccountConfirmation() {
        const url = `${AppConfig.settings.apiSignupUrl}ResendAccountConfirmation`;
        return this.http.get<boolean>(url).pipe(
        catchError(e => {
            this.dialog.reportError(ErrorLevels.Critical, "Resend account confirmation", "An error occurred trying to complete your payment request. ", e);
            return of<boolean>(null);
        })
        );
    }

    recoverPassword(email: string) {
        const url = `${AppConfig.settings.apiAuthUrl}resetRequest`;
        const data = { email: email };
        return this.http.post<boolean>(url, data).
            pipe(
                catchError(e => {
                    this.dialog.reportError(ErrorLevels.Critical, "Recover password", "An error occurred while requesting recover password action. ", e);
                    return of<boolean>(false);
                })
            );
    }

    confirmResetToken(id: string) {
        const url = `${AppConfig.settings.apiAuthUrl}confirmResetToken/${id}`;
        return this.http.post<boolean>(url, null).pipe(
            catchError(e => {
                this.dialog.reportError(ErrorLevels.Critical, "Validate reset token", "An error occurred while confirming password request is valid. ", e);
                return of<boolean>(false);
            })
        );
    }

    async resetPassword(model: authLib.userPasswordUpdateModel) {
        const authResult = await firstValueFrom(this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}reset`, model)
            .pipe(
                catchError(e => {
                    this.dialog.reportError(ErrorLevels.Critical, "Fatal error", "The authentication service encountered an error while trying to reset your password.  The app cannot continue while this condition continues.  Please try again later. ", e);
                    return of<any>({
                        result: ApiResultValues.Exception,
                        message: 'Unknown API Error',
                        data: []
                    });
                })
            ));

        if (authResult.result === 'Ok' || authResult.result === 'AwaitingVerification') {
            const uiRes = this.handleLoginResult(authResult, authLib.AuthenticationStatusPurpose.PasswordChange);
            return uiRes;
        } else {
            const res: authLib.uiAuthResult = {
                error: authResult.result,
                ok: false,
                requireVerification: false
            };

            return res;
        }

        
        // return new Observable<authLib.uiAuthResult>(o => {
        //     const f = async () => {
        //         const authResult = await firstValueFrom(this.http.post<authLib.serviceAuthenticationResult>(`${AppConfig.settings.apiAuthUrl}reset`, model)
        //             .pipe(
        //                 catchError(e => {
        //                     this.dialog.reportError(ErrorLevels.Critical, "Fatal error", "The authentication service encountered an error while trying to reset your password.  The app cannot continue while this condition continues.  Please try again later. ", e);
        //                     return of<any>({
        //                         result: ApiResultValues.Exception,
        //                         message: 'Unknown API Error',
        //                         data: []
        //                     });
        //                 })
        //             ));

        //         if (authResult.result === 'Ok' || authResult.result === 'AwaitingVerification') {
        //             const uiRes = this.handleLoginResult(authResult, authLib.AuthenticationStatusPurpose.PasswordChange);
        //             o.next(uiRes);
        //         } else {
        //             const res: authLib.uiAuthResult = {
        //                 error: authResult.result,
        //                 ok: false,
        //                 requireVerification: false
        //             };

        //             o.next(res);
        //         }
        //     };

        //     f();
        // });
    }

    unlockAccount(id: string) {
        return this.http.post<boolean>(`${AppConfig.settings.apiAuthUrl}unlock/${id}`, null);
    }

//   loginViaDialog() {
//     return new Observable<boolean>(o => {
//       this.dialog.open(LoginUserComponent,
//         {
//           height: '500px',
//           width: '750px'
//         }).afterClosed().subscribe(res => {
//           if (res) {
//             //Only returns true if successful login
//             o.next(true);
//           } else {
//             o.next(false);
//           }
//         })
//     });
//   }



    disconnectSso() {
        //send this users
        return new Observable<boolean>(o => {
        return this.http.post<boolean>(`${AppConfig.settings.apiAuthUrl}disconnectSso/${this.getCurrentUser.ssoProvider}`, null).subscribe(r => {
            if (r) {
            const u = Object.assign({}, this.currentUserSubject.value);
            u.user.ssoLogin = false;
            u.user.ssoProvider = '';
            u.user.ssoUuid = '';
            this.currentUserSubject.next(u);
            o.next(true);
            } else {
            o.next(false);
            }
        });
        });
    }

  

    clearStorageAndLogout(){
        localStorage.clear();
        if(this.isAuthenticated.value){
            this.logout(null, authLib.AuthenticationStatusPurpose.Logout);
        }
    }
}
